import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect, ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { DialogType } from "app/types";
import { uploadImageFile } from "app/api";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useUserConfig } from "app/hook";
import Constants from "common/constant";
import { dataURLToBlob } from "common/utils";
import i18n from "features/parent/i18n";
import { sendContactChatRoomMessage } from "features/user/api";
import { ChatRoomType, MessageContentType } from "features/user/types";
import { getParentChatRooms } from "../api";
import { ParentModal } from "./ParentModal";
import { selectAllParentChatRooms } from "../slice";

const DETAIL_MAX_LENGTH = 500;

// axios のエラーは message だけだと原因が分からないので、status と本文も拾う
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const describeError = (error: any) => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const text = typeof body === "string" ? body : body ? JSON.stringify(body) : "";
  return [status && `HTTP ${status}`, text, error?.message]
    .filter(Boolean)
    .join(" / ")
    .slice(0, 300);
};

/**
 * 保護者ページで端末を振ったときに出る報告ダイアログ。
 *
 * 子ども向けの確認ダイアログではなく、この画面だけで完結させ、
 * 入力した内容と画面キャプチャをサポートのチャットへ送る。
 * kids は横画面前提なので、保護者ページ標準の ParentModal に
 * キャプチャ（左）と入力（右）を横に並べて収める。
 */
export const ParentShakeBugReportDialog = () => {
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const handleChangeUserConfig = useUserConfig();
  const parent = useSelector((state: RootState) => state.user.parent);
  const chatRooms = useSelector(selectAllParentChatRooms);
  const shakeBugReport = useSelector(
    (state: RootState) => state.config.userConfig.shake_bug_report,
  );
  const shake = useSelector((state: RootState) => state.config.dialog.parent_shake_bug_report);
  const capture = shake.args?.capture;

  const [detail, setDetail] = useState("");
  const [sending, setSending] = useState(false);
  // 添付画像。振ったときのキャプチャを初期値にし、外したら選び直せる
  const [image, setImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ネイティブではコンソールを見られないので、失敗の理由は画面に出す
  const [errorDetail, setErrorDetail] = useState("");

  const contactRoomId = chatRooms.find((room) => room.type === ChatRoomType.CONTACT)?.id;

  useEffect(() => {
    if (shake.value) setImage(capture ?? null);
  }, [shake.value, capture]);

  // メッセージタブを開いていない画面から振られるとチャットルーム一覧が
  // store に無く送信先が引けない。開いた時点で取りにいく
  useEffect(() => {
    if (shake.value && !contactRoomId && parent?.uid) {
      dispatch(getParentChatRooms({ uid: parent.uid }));
    }
  }, [shake.value, contactRoomId, parent?.uid, dispatch]);

  const handleClose = () => {
    setDetail("");
    setImage(null);
    setErrorDetail("");
    handleDialog({ type: DialogType.PARENT_SHAKE_BUG_REPORT, value: false, args: {} });
  };

  const handleChangeDetail = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDetail(e.target.value.slice(0, DETAIL_MAX_LENGTH));
  };

  const handleToggleShake = () => {
    handleChangeUserConfig("shake_bug_report", !shakeBugReport, true);
  };

  // 添付は必須ではないので、アップロードに失敗しても本文だけ送る
  const uploadCapture = async () => {
    if (!image || !parent?.uid) return undefined;
    try {
      const filename = `parents/${parent.uid}/shake_capture/${uuidv4()}.jpg`;
      await uploadImageFile({
        fileBlob: dataURLToBlob(image),
        filename,
        contentType: "image/jpeg",
      });
      return `${Constants.assetHost}/${filename}`;
    } catch (error) {
      console.error("Failed to upload capture:", error);
      setErrorDetail(`upload: ${describeError(error)}`);
      return undefined;
    }
  };

  const handleSend = async () => {
    if (sending) return;
    if (!parent?.uid) {
      setErrorDetail("uid なし（ログイン状態を確認してください）");
      return;
    }
    if (!contactRoomId) {
      setErrorDetail("サポートのチャットが見つかりません（room 未取得）");
      return;
    }
    setSending(true);
    setErrorDetail("");
    try {
      const imageUrl = await uploadCapture();
      const text = detail.trim();
      const hasImage = !!imageUrl;
      await sendContactChatRoomMessage({
        uid: parent.uid,
        roomId: contactRoomId,
        message: {
          url: `${window.location.pathname}${window.location.search}`,
          message_content_type: hasImage
            ? text
              ? MessageContentType.IMAGE_WITH_TEXT
              : MessageContentType.IMAGE
            : MessageContentType.TEXT,
          message_content: hasImage
            ? text
              ? { url: imageUrl, text }
              : { url: imageUrl }
            : { text },
        },
      });
      toast(i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_SENT"));
      handleClose();
    } catch (error) {
      console.error("Failed to send bug report:", error);
      setErrorDetail(`send: ${describeError(error)}`);
    } finally {
      setSending(false);
    }
  };

  // 添付は任意。本文があれば送れる（ルーム未取得は押した時に理由を出す）
  const canSend = !sending && (!!detail.trim() || !!image);

  const handlePickImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImage(String(reader.result));
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <ParentModal
      open={shake.value}
      onClose={handleClose}
      icon="bug_report"
      title={i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_BUG")}
    >
      {/* 横画面なので縦に積まず、キャプチャと入力を左右に並べる。
          添付を外しても枠とボタンの大きさは変えず、中身だけ差し替える */}
      <div className="flex-row-el gap-6 w-full">
        <div className="flex-col-el gap-3 w-[360px] shrink-0">
          <div className="flex-col-el flex-center w-full h-[240px] box-border overflow-hidden rounded-[12px] border border-solid border-gray2-60 bg-gray2-20">
            {image ? (
              <img alt="" src={image} className="w-full h-full object-contain" />
            ) : (
              <div className="flex-col-el flex-center gap-2">
                <span className="material-symbols-outlined text-gray2-100 !text-[48px]">
                  hide_image
                </span>
                <div className="text-[21px] text-gray2-100">
                  {i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_ATTACH_OFF")}
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePickImage}
            className="hidden"
          />
          <button
            onClick={image ? () => setImage(null) : () => fileInputRef.current?.click()}
            className={`flex-row-el items-center justify-center gap-2 w-full h-[56px] shrink-0 box-border rounded-[12px] bg-transparent border-2 border-solid cursor-pointer text-[21px] transition-all ${
              image
                ? "border-danger text-danger hover:bg-danger hover:text-white"
                : "border-gray2-60 text-textcolor-black hover:bg-gray2-30"
            }`}
          >
            <span className="material-symbols-outlined !text-[28px]">
              {image ? "delete" : "add_photo_alternate"}
            </span>
            {image
              ? i18n.t("MSG_PARENT_PAGE_DELETE_BTN")
              : i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_ATTACH_PICK")}
          </button>
        </div>

        <div className="flex-col-el flex-1 gap-3">
          <div className="text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_DESCRIPTION")}
          </div>

          <textarea
            value={detail}
            onChange={handleChangeDetail}
            placeholder={i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_PLACEHOLDER")}
            className="resize-none flex-1 w-full box-border p-4 rounded-[12px] border border-solid border-gray2-60 text-[24px] text-textcolor-black"
          />

          <button
            onClick={handleSend}
            disabled={!canSend}
            className="flex-row-el items-center justify-center gap-3 w-full h-[56px] shrink-0 bg-blue-100 border-none text-white rounded-[12px] text-[24px] cursor-pointer disabled:bg-gray2-60 disabled:cursor-not-allowed hover:opacity-80 transition-all"
          >
            <span className="material-symbols-outlined !text-[28px] text-white">send</span>
            {sending
              ? i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_SENDING")
              : i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_BTN")}
          </button>
        </div>
      </div>

      {errorDetail && (
        <div className="text-[20px] text-red-100 break-all">
          {i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_SEND_FAILED")}
          {"\n"}
          {errorDetail}
        </div>
      )}

      {/* トグルは保護者ページの設定行と同じ見た目にそろえる */}
      <div className="flex-col-el gap-1 w-full pt-4 border-t border-x-0 border-b-0 border-solid border-outline">
        <div className="flex-row-el items-center justify-between gap-4 w-full">
          <div className="text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT")}
          </div>
          <button
            onClick={handleToggleShake}
            className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
              shakeBugReport ? "bg-green-100" : "bg-gray2-100"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                shakeBugReport ? "translate-x-[24px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        <div className="text-[20px] text-gray2-100">
          {i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT_TOGGLE_DESCRIPTION")}
        </div>
      </div>
    </ParentModal>
  );
};
